import React, { useState, useEffect } from "react";
import { cloneDeep } from "lodash";

import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { Box } from "@mui/material";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

import DevicePanel from "./DevicePanel";
import NetWorkPanel from "./NetworkPanel";
import ManagementPanel from "./ManagementPanel";
import RadioPanel from "./RadioPanel";
import AutoProvisionPanel from "./AutoProvisionPanel";
import SoftwareUpdatePanel from "./SoftwareUpdatePanel";

import { formLimitation } from "const";
import { isValidIpV4, isValidSubnetMask, isSameSubnet } from "utils/network-validation";

const getDefaultIpv4Data = (ipv4 = {}) => ({
  gateway: ipv4?.gateway ?? null,
  ipAddress: ipv4?.ipAddress ?? null,
  primaryDns: ipv4?.primaryDns ?? null,
  secondaryDns: ipv4?.secondaryDns ?? null,
  subnetMask: ipv4?.subnetMask ?? null,
  type: ipv4?.type || "dhcp",
});

const getDefaultIpv6Data = (ipv6 = {}) => ({
  gateway: ipv6?.gateway ?? null,
  ipAddress: ipv6?.ipAddress ?? null,
  prefixLength: ipv6?.prefixLength ?? null,
  primaryDns: ipv6?.primaryDns ?? null,
  secondaryDns: ipv6?.secondaryDns ?? null,
  type: ipv6?.type || "disable",
});

const SettingsContent = ({
  auth,
  capabilityData,
  infoData,
  isInventoryDevice,
  fetchTableData,
  settingNetworkPermissions,
  handleDrawerClose,
  settingsData
}) => {
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [deviceApplyUuid, _setDeviceApplyUuid] = useState(uuidv4());
  const [networkApplyUuid, _setNetworkApplyUuid] = useState(uuidv4());
  const [radioApplyUuid, _setRadioApplyUuid] = useState(uuidv4());
  const [proxyAutoProvisionApplyUuid, _setProxyAutoProvisionApplyUuid] =
    useState(uuidv4());

  const [dataChangesFlag, setDataChangesFlag] = useState({
    device: false,
    netWork: false,
    radio: false,
    proxyAutoProvision: false,
  });
  const [dataCancelFlag, setDataCancelFlag] = useState({
    device: false,
    netWork: false,
    radio: false,
    proxyAutoProvision: false,
  });
  const [requestDataMap, setRequestDataMap] = useState({
    device: null,
    netWork: null,
    radio: null,
    proxyAutoProvision: null,
  });

  const [networkUuid, _setNetworkUuid] = useState(uuidv4());
  const [settingsGeneralUuid, _setSettingsGeneralUuid] = useState(uuidv4());

  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [networkInfoData, setNetworkInfoData] = useState({});
  const [networkDeviceData, setNetworkDeviceData] = useState({});
  const [ipv4Data, setIpv4Data] = useState({});
  const [ipv6Data, setIpv6Data] = useState({});
  const [wirelessRadioData, setWirelessRadioData] = useState({});
  const [proxyAutoProvisionData, setProxyAutoProvisionData] = useState({});
  const [settingsGeneralData, setSettingsGeneralData] = useState({});

  const [deviceNameError, setDeviceNameError] = useState("");

  const sendSettingsGeneralRequest = () => {
    const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;
    const baseOrgUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
    const baseSiteUrl = standalone
      ? `/orgs/:orgId/sites/:siteId`
      : `/ents/:entId/orgs/:orgId/sites/:siteId`;

    const requestConfig = {
      org: {
        apiKey: `${baseOrgUrl}/settings/general.read`,
        restfulParams: standalone
          ? { orgId: auth.realm.id }
          : { entId: auth.realm.id, orgId: auth.orgs.id },
      },
      site: {
        apiKey: `${baseSiteUrl}/settings/general.read`,
        restfulParams: standalone
          ? { orgId: auth.realm.id, siteId: auth.sites?.id }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            siteId: auth.sites?.id,
          },
      },
    };

    const readConfig = requestConfig[auth.scopeLevel];
    sendRequest(
      readConfig.apiKey,
      settingsGeneralUuid,
      readConfig.restfulParams,
    );
  };

  useEffect(() => {
    setLoading(true);
    const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;
    const baseOrgUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
    const baseSiteUrl = standalone
      ? `/orgs/:orgId`
      : `/ents/:entId/orgs/:orgId/sites/:siteId`;

    const deviceIdValue =
      infoData.deviceType === "proxy" ? infoData.id : infoData.macAddress;

    const baseRestfulParams = standalone
      ? {
        orgId: auth.realm.id,
        deviceId: deviceIdValue,
      }
      : {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        deviceId: deviceIdValue,
      };

    const requestConfig = {
      org: {
        apiKey: `${baseOrgUrl}/devices/:deviceId/settings.read`,
        restfulParams: baseRestfulParams,
      },
      site: {
        apiKey: `${baseSiteUrl}/devices/:deviceId/settings.read`,
        restfulParams: { ...baseRestfulParams, siteId: auth.sites?.id },
      },
    };

    const readNetWorkConfig = requestConfig[auth.scopeLevel];

    sendRequest(
      readNetWorkConfig.apiKey,
      networkUuid,
      readNetWorkConfig.restfulParams,
    );
  }, []);

  useEffect(() => {
    const applyResponse = getData(deviceApplyUuid);
    if (
      typeof fetchTableData === "function" &&
      applyResponse &&
      applyResponse.data
    ) {
      fetchTableData();
    }

    const networkResponse = getData(networkUuid);
    if (networkResponse) {
      if (networkResponse.data) {
        const data = networkResponse.data;
        const cloneData = cloneDeep(data);

        cloneData.info = {
          ...cloneData.info,
          deviceName: data.info.deviceName || "",
          address: data.info.address || "",
        };
        setOriginalData(cloneData);

        const deviceNetworkData = data.deviceNetwork;
        setNetworkInfoData(cloneData.info);
        setNetworkDeviceData({ mgmtVlan: deviceNetworkData?.mgmtVlan });
        setIpv4Data(getDefaultIpv4Data(deviceNetworkData?.ipv4));
        setIpv6Data(getDefaultIpv6Data(deviceNetworkData?.ipv6));
        setWirelessRadioData(data["wireless.radio"]);
        setProxyAutoProvisionData(data["proxy.autoProvision"]);

        // Fetch settings general data
        sendSettingsGeneralRequest();
      } else {
        setLoading(false);
      }
    }

    const settingsGeneralResponse = getData(settingsGeneralUuid);
    if (settingsGeneralResponse) {
      if (settingsGeneralResponse) {
        if (settingsGeneralResponse.data) {
          const data = settingsGeneralResponse.data;
          setSettingsGeneralData(data);
        }

        setLoading(false);
      }
    }
  }, [dataMap]);

  // 如果後續 API 都是同一支 api 有緣再整理
  const actionConfig = {
    device: {
      onCancel: () => {
        setDataCancelFlag((prev) => ({ ...prev, device: true }));
      },
      onApply: (requestParams) => {
        requestParams.data.info.deviceName =
          requestParams.data.info.deviceName || null;
        requestParams.data.info.address =
          requestParams.data.info.address || null;

        const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;

        const deviceIdValue =
          infoData.deviceType === "proxy" ? infoData.id : infoData.macAddress;

        const baseRestfulParams = standalone
          ? {
            orgId: auth.realm.id,
            deviceId: deviceIdValue,
          }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            deviceId: deviceIdValue,
          };

        const baseOrgUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId`;
        const baseSiteUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId/sites/:siteId`;

        const requestConfig = {
          org: {
            apiKey: `${baseOrgUrl}/devices/:deviceId/settings.update`,
            restfulParams: baseRestfulParams,
          },
          site: {
            apiKey: `${baseSiteUrl}/devices/:deviceId/settings.update`,
            restfulParams: { ...baseRestfulParams, siteId: auth.sites?.id },
          },
        };

        const applyConfig = requestConfig[auth.scopeLevel];

        sendRequest(
          applyConfig.apiKey,
          deviceApplyUuid,
          applyConfig.restfulParams,
          requestParams,
        );
      },
    },
    netWork: {
      onCancel: () => {
        setDataCancelFlag((prev) => ({ ...prev, netWork: true }));
      },
      onApply: (requestParams) => {
        const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;
        const baseOrgUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId`;
        const baseSiteUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId/sites/:siteId`;

        const requestConfig = {
          org: {
            apiKey: `${baseOrgUrl}/devices/:deviceId/settings.update`,
            restfulParams: standalone
              ? { orgId: auth.realm.id, deviceId: infoData.macAddress }
              : {
                entId: auth.realm.id,
                orgId: auth.orgs.id,
                deviceId: infoData.macAddress,
              },
          },
          site: {
            apiKey: `${baseSiteUrl}/devices/:deviceId/settings.update`,
            restfulParams: standalone
              ? { orgId: auth.realm.id, siteId: auth.sites?.id, deviceId: infoData.macAddress }
              : {
                entId: auth.realm.id,
                orgId: auth.orgs.id,
                siteId: auth.sites?.id,
                deviceId: infoData.macAddress,
              },
          },
        };

        const applyConfig = requestConfig[auth.scopeLevel];
        sendRequest(
          applyConfig.apiKey,
          networkApplyUuid,
          applyConfig.restfulParams,
          requestParams,
        );
      },
    },
    radio: {
      onCancel: () => {
        setDataCancelFlag((prev) => ({ ...prev, radio: true }));
      },
      onApply: (requestParams) => {
        const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;
        const baseOrgUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId`;
        const baseSiteUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId/sites/:siteId`;

        const requestConfig = {
          org: {
            apiKey: `${baseOrgUrl}/devices/:deviceId/settings.update`,
            restfulParams: standalone
              ? { orgId: auth.realm.id, deviceId: infoData.macAddress }
              : {
                entId: auth.realm.id,
                orgId: auth.orgs.id,
                deviceId: infoData.macAddress,
              },
          },
          site: {
            apiKey: `${baseSiteUrl}/devices/:deviceId/settings.update`,
            restfulParams: standalone
              ? { orgId: auth.realm.id, siteId: auth.sites?.id, deviceId: infoData.macAddress }
              : {
                entId: auth.realm.id,
                orgId: auth.orgs.id,
                siteId: auth.sites?.id,
                deviceId: infoData.macAddress,
              },
          },
        };

        const applyConfig = requestConfig[auth.scopeLevel];
        sendRequest(
          applyConfig.apiKey,
          radioApplyUuid,
          applyConfig.restfulParams,
          { data: requestParams },
        );
      },
    },
    proxyAutoProvision: {
      onCancel: () => {
        setDataCancelFlag((prev) => ({ ...prev, proxyAutoProvision: true }));
      },
      onApply: (requestParams) => {
        requestParams.data["proxy.autoProvision"] = {
          enable: !!requestParams.data["proxy.autoProvision"]?.enable,
        };

        const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;

        const deviceIdValue =
          infoData.deviceType === "proxy" ? infoData.id : infoData.macAddress;

        const baseRestfulParams = standalone
          ? {
            orgId: auth.realm.id,
            deviceId: deviceIdValue,
          }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            deviceId: deviceIdValue,
          };

        const baseOrgUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId`;
        const baseSiteUrl = standalone
          ? `/orgs/:orgId`
          : `/ents/:entId/orgs/:orgId/sites/:siteId`;

        const requestConfig = {
          org: {
            apiKey: `${baseOrgUrl}/devices/:deviceId/settings.update`,
            restfulParams: baseRestfulParams,
          },
          site: {
            apiKey: `${baseSiteUrl}/devices/:deviceId/settings.update`,
            restfulParams: { ...baseRestfulParams, siteId: auth.sites?.id },
          },
        };

        const applyConfig = requestConfig[auth.scopeLevel];

        sendRequest(
          applyConfig.apiKey,
          proxyAutoProvisionApplyUuid,
          applyConfig.restfulParams,
          requestParams,
        );
      },
    },
  };

  const handleDataChanged = (flagKey, requestData) => {
    setDataChangesFlag((prev) => ({ ...prev, [flagKey]: true }));
    setRequestDataMap((prev) => ({ ...prev, [flagKey]: requestData }));
  };

  const handleCancel = () => {
    Object.entries(dataChangesFlag).forEach(([key, changed]) => {
      if (changed && requestDataMap[key] && actionConfig[key]?.onCancel) {
        actionConfig[key].onCancel();
      }
    });

    const resetFlags = {};
    const resetRequestData = {};
    Object.keys(dataCancelFlag).forEach((key) => {
      resetFlags[key] = false;
      resetRequestData[key] = null;
    });
    setDataChangesFlag(resetFlags);
    setRequestDataMap(resetRequestData);
  };

  const handleApply = () => {
    Object.entries(dataChangesFlag).forEach(([key, changed]) => {
      if (changed && requestDataMap[key] && actionConfig[key]?.onApply) {
        actionConfig[key].onApply(requestDataMap[key]);
      }
    });

    const resetFlags = {};
    Object.keys(dataChangesFlag).forEach((key) => {
      resetFlags[key] = false;
    });

    setDataChangesFlag(resetFlags);
    setRequestDataMap(resetFlags);
    if (typeof fetchTableData === "function") {
      fetchTableData();
    }
  };

  const isProxy = infoData?.deviceType === "proxy";
  const isSurveillanceDevice = ["nvr", "ipcam"].includes(infoData?.deviceType);
  const isNvr = infoData?.deviceType === "nvr";
  const shouldShowNetworkPanel =
    !isProxy && (isSurveillanceDevice || infoData.managedProtocol !== "snmp");

  const isFormInValid = () => {
    let isInValid = false;

    if (deviceNameError) isInValid = true;
    if (
      !isSurveillanceDevice &&
      +networkDeviceData?.mgmtVlan < formLimitation.vlanId.min ||
      !isSurveillanceDevice &&
      +networkDeviceData?.mgmtVlan > formLimitation.vlanId.max
    )
      isInValid = true;

    if (ipv4Data?.type === "static") {
      const { ipAddress, subnetMask, gateway, primaryDns, secondaryDns } = ipv4Data;

      if (!ipAddress || !isValidIpV4(ipAddress)) {
        isInValid = true;
      }
      if (!subnetMask || !isValidIpV4(subnetMask)) {
        isInValid = true;
      }
      if (
        !gateway ||
        !isValidIpV4(gateway) ||
        !isSameSubnet(ipAddress, gateway, subnetMask)
      ) {
        isInValid = true;
      }
      if (isNvr) {
        if (primaryDns && !isValidIpV4(primaryDns)) {
          isInValid = true;
        }
      } else if (!primaryDns || !isValidIpV4(primaryDns)) {
        isInValid = true;
      }
      if (secondaryDns && !isValidIpV4(secondaryDns)) {
        isInValid = true;
      }
    }

    return isInValid;
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        boxSizing: "border-box",
        gap: "20px",
      }}
    >
      <Box width="100%" padding="16px">
        <DevicePanel
          loading={loading}
          viewerPermissions={auth.permissions === "Viewer"}
          setDataChangesFlag={setDataChangesFlag}
          handleDataChanged={(data) => handleDataChanged("device", data)}
          dataCancelFlag={dataCancelFlag}
          setDataCancelFlag={setDataCancelFlag}
          originalData={originalData}
          setOriginalData={setOriginalData}
          networkInfoData={networkInfoData}
          setNetworkInfoData={setNetworkInfoData}
          deviceNameError={deviceNameError}
          setDeviceNameError={setDeviceNameError}
        />
      </Box>

      {shouldShowNetworkPanel && (
        <Box width="100%" padding="16px">
          <NetWorkPanel
            loading={loading}
            viewerPermissions={auth.permissions === "Viewer"}
            networkPermissions={settingNetworkPermissions}
            setDataChangesFlag={setDataChangesFlag}
            handleDataChanged={(data) => handleDataChanged("netWork", data)}
            dataCancelFlag={dataCancelFlag}
            setDataCancelFlag={setDataCancelFlag}
            originalData={originalData}
            setOriginalData={setOriginalData}
            networkInfoData={networkInfoData}
            setNetworkInfoData={setNetworkInfoData}
            networkDeviceData={networkDeviceData}
            setNetworkDeviceData={setNetworkDeviceData}
            ipv4Data={ipv4Data}
            setIpv4Data={setIpv4Data}
            ipv6Data={ipv6Data}
            setIpv6Data={setIpv6Data}
            capabilityData={capabilityData}
            infoData={infoData}
          />
        </Box>
      )}

      {!isProxy && infoData.deviceType === "ap" && (
        <Box width="100%" padding="16px">
          <RadioPanel
            infoData={infoData}
            viewerPermissions={auth.permissions === "Viewer"}
            capabilityData={capabilityData}
            settingsGeneralData={settingsGeneralData}
            wirelessRadioData={wirelessRadioData}
            setWirelessRadioData={setWirelessRadioData}
            originalData={originalData}
            setOriginalData={setOriginalData}
            setDataChangesFlag={setDataChangesFlag}
            handleDataChanged={(data) => handleDataChanged("radio", data)}
            dataCancelFlag={dataCancelFlag}
            setDataCancelFlag={setDataCancelFlag}
          />
        </Box>
      )}
      {/* 0.7.? */}
      {isProxy && (
        <Box width="100%" padding="16px">
          <AutoProvisionPanel
            loading={loading}
            viewerPermissions={auth.permissions === "Viewer"}
            proxyAutoProvisionData={proxyAutoProvisionData}
            handleDataChanged={(data) =>
              handleDataChanged("proxyAutoProvision", data)
            }
            dataCancelFlag={dataCancelFlag}
            setDataCancelFlag={setDataCancelFlag}
          />
        </Box>
      )}
      {(auth.permissions === "Super Admin" || auth.permissions === "Admin") && (
        <Box width="100%" padding="16px">
          <ManagementPanel
            auth={auth}
            capabilityData={capabilityData}
            infoData={infoData}
            isInventoryDevice={isInventoryDevice}
            fetchTableData={fetchTableData}
            handleDrawerClose={handleDrawerClose}
            settingsData={settingsData}
          />
        </Box>
      )}
      {isProxy && (
        <Box width="100%" padding="16px">
          <SoftwareUpdatePanel auth={auth} infoData={infoData} />
        </Box>
      )}

      {Object.values(dataChangesFlag).some(Boolean) && (
        <Box
          sx={{
            position: "sticky",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            gap: 2,
            padding: "16px",
            backgroundColor: "primary.lightBlue",
            bottom: 0,
          }}
        >
          <OutlineButton width="100px" height="32px" onClick={handleCancel}>
            Cancel
          </OutlineButton>
          <SolidButton
            width="100px"
            height="32px"
            onClick={handleApply}
            disabled={isFormInValid()}
          >
            Apply
          </SolidButton>
        </Box>
      )}
    </Box>
  );
};

export default SettingsContent;
